/**
 * Admin auth helpers.
 *
 * Since the migration to httpOnly cookies, the actual session token is no
 * longer visible to JavaScript — the browser attaches it automatically to
 * every credentialed request. We only keep a tiny non-secret flag in
 * `sessionStorage` (`admin_ui`) so the client-side router can pick the right
 * "home" destination without an extra network round-trip.
 *
 * SECURITY NOTE
 * -------------
 * `admin_ui` does NOT authenticate anything — it's a UI hint. If a user
 * fakes it, they'll be redirected to /admin but every request there still
 * requires the httpOnly cookie, so nothing sensitive leaks.
 *
 * BACKWARD COMPATIBILITY
 * ----------------------
 * `localStorage.admin_token` is still read as a fallback for admins who
 * logged in before the migration (their httpOnly cookie hasn't been set yet).
 * These will naturally drain within 24 h (session TTL) and can be removed
 * from the codebase in a follow-up.
 */

const ADMIN_UI_FLAG = 'admin_ui';

export const setAdminUiFlag = () => {
  try { sessionStorage.setItem(ADMIN_UI_FLAG, '1'); } catch (e) { console.warn('[adminAuth] sessionStorage write blocked:', e); }
};

export const clearAdminUiFlag = () => {
  try { sessionStorage.removeItem(ADMIN_UI_FLAG); } catch (e) { console.warn('[adminAuth] sessionStorage clear blocked:', e); }
  // Also clear legacy localStorage token if present (post-migration cleanup)
  try { localStorage.removeItem('admin_token'); } catch (e) { /* noop */ }
};

/**
 * Best-effort synchronous "is the user probably logged in as admin?" check.
 * Used for client-side routing decisions only. Real auth is enforced by the
 * server via the httpOnly cookie on every /api/admin/* call.
 */
export const hasAdminUiFlag = () => {
  try {
    if (sessionStorage.getItem(ADMIN_UI_FLAG)) return true;
    // Fallback for legacy sessions still using localStorage token
    if (localStorage.getItem('admin_token')) return true;
  } catch (e) { /* noop */ }
  return false;
};
